/**
 * F14.3 — ResetPassword refatorado para design system v2.1 + i18n.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import {
  AlertError,
  AlertSuccess,
  AuthBrand,
  AuthShell,
  FieldLabel,
  GhostLink,
  PillInput,
  PrimaryButton,
} from "../components/auth/AuthShell.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

function getToken(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const t = useT();
  const token = getToken();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const statusQuery = trpc.passwordReset.checkToken.useQuery(
    { token },
    { enabled: token.length > 0, retry: false, refetchOnWindowFocus: false },
  );

  const confirmMutation = trpc.passwordReset.confirm.useMutation({
    onSuccess: () => {
      setDone(true);
      setTimeout(() => setLocation("/login"), 2000);
    },
    onError: (err) => setError(err.message),
  });

  const pending = confirmMutation.isPending;
  const status = statusQuery.data?.status;
  const tokenInvalid =
    !token || status === "not_found" || status === "expired" || status === "used";

  return (
    <AuthShell>
      <div className="w-full max-w-[430px]">
        <AuthBrand subtitle={t("auth.resetTitle")} />

        {!token || tokenInvalid ? (
          <div className="space-y-6">
            <AlertError>{t("auth.errorTokenInvalid")}</AlertError>
            <GhostLink href="/esqueci-senha">{t("auth.forgotPasswordLink")}</GhostLink>
          </div>
        ) : statusQuery.isLoading ? (
          <p
            className="text-center"
            style={{
              fontFamily: "var(--font-body)",
              color: "var(--text-secondary)",
            }}
          >
            {t("common.loading")}
          </p>
        ) : done ? (
          <div className="space-y-6">
            <AlertSuccess>{t("auth.resetSuccess")}</AlertSuccess>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (password !== confirmPassword) {
                setError(t("auth.errorPasswordsDoNotMatch"));
                return;
              }
              confirmMutation.mutate({ token, password });
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 14,
                color: "var(--text-secondary)",
                textAlign: "center",
                marginBottom: 24,
                lineHeight: 1.5,
              }}
            >
              {t("auth.resetDescription")}
            </p>

            <FieldLabel htmlFor="password">{t("auth.newPasswordLabel")}</FieldLabel>
            <PillInput
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={setPassword}
              focused={passwordFocused}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              disabled={pending}
              autoFocus
              placeholder={t("auth.passwordPlaceholder")}
            />

            <div className="mt-5">
              <FieldLabel htmlFor="confirmPassword">{t("auth.confirmPasswordLabel")}</FieldLabel>
              <PillInput
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={setConfirmPassword}
                focused={confirmFocused}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                disabled={pending}
                placeholder={t("auth.passwordPlaceholder")}
              />
            </div>

            {error && (
              <div className="mt-5">
                <AlertError>{error}</AlertError>
              </div>
            )}

            <div className="mt-7">
              <PrimaryButton disabled={pending}>
                {pending ? t("auth.submitResetPending") : t("auth.submitReset")}
              </PrimaryButton>
            </div>

            <div className="mt-6">
              <GhostLink href="/login">{t("auth.backToLoginLink")}</GhostLink>
            </div>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
